import { Link } from "react-router-dom";
import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { SERVICES } from "@/lib/services";
import {
  ArrowUpRight, Code, ChatCircle, Robot, Calendar, Kanban, Sparkle,
  Check, ArrowRight,
} from "@phosphor-icons/react";

const SVC_ICON = { website: Code, crm: Kanban, booking: Calendar, chatbot: ChatCircle, automation: Sparkle, workflow: Robot };

const PLANS = [
  {
    name: "Discover",
    tag: "Understand the requirement",
    features: ["Business requirement review", "Workflow and constraint mapping", "Technical feasibility assessment", "Proposed scope and next steps"],
    cta: "View launch status",
    accent: false,
  },
  {
    name: "Build",
    tag: "Deliver an agreed solution",
    features: ["Written scope and milestones", "Implementation and testing", "Deployment planning", "Operational documentation"],
    cta: "View launch status",
    accent: true,
  },
  {
    name: "Operate",
    tag: "Support after delivery",
    features: ["Maintenance options by agreement", "Monitoring requirements", "Controlled change requests", "Periodic service review"],
    cta: "View launch status",
    accent: false,
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-[#172033]">
      <TopNav />

      {/* ===== HERO ===== */}
      <section className="relative border-b border-[#DCE3ED] overflow-hidden bg-white">
        <div className="fx-grid-lines absolute inset-0 opacity-60 pointer-events-none" />
        <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20 lg:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 relative">
          <div className="col-span-1 md:col-span-8 fx-fade-up">
            <div className="fx-mono-tag mb-5">◇ AGENAIX · AI AGENTS & AUTOMATION INFRASTRUCTURE</div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
              AI agents and automation{" "}
              <br className="hidden sm:block" />
              infrastructure for <span className="text-[#0B4DCC]">modern businesses.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-[#475569] leading-relaxed">
              Request a service, collaborate with the Agenaix Assistant, and track delivery from one workspace.
              Operational systems designed around your business requirements.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup">
                <Button className="fx-btn-primary h-11 sm:h-12 px-5 sm:px-6 rounded-lg text-sm font-semibold" data-testid="hero-cta-start">
                  View launch status <ArrowUpRight size={16} weight="bold" className="ml-2" />
                </Button>
              </Link>
              <a href="#services">
                <Button variant="outline" className="h-11 sm:h-12 px-5 sm:px-6 rounded-lg text-sm font-semibold border-[#DCE3ED] hover:bg-[#F4F6F8] transition-colors" data-testid="hero-cta-pricing">
                  Explore services
                </Button>
              </a>
            </div>
          </div>

          <div className="col-span-1 md:col-span-4 flex md:justify-end items-end">
            <div className="border border-[#DCE3ED] bg-white p-6 w-full md:max-w-xs rounded-xl fx-fade-up shadow-[0_2px_12px_rgba(11,31,58,0.06)]" style={{animationDelay:'.15s'}}>
              <div className="fx-mono-tag mb-3">WHAT WE DO</div>
              <div className="text-sm text-[#172033] leading-relaxed">
                Agenaix is building a secure workspace for requesting and managing websites, CRMs, booking systems, AI chatbots, workflow automation, and internal tools.
              </div>
              <div className="mt-5 pt-5 border-t border-[#DCE3ED] flex items-center gap-2 text-xs text-[#64748B]">
                <div className="w-2 h-2 rounded-full bg-[#D97706]" />
                <span>Pre-launch preparation in progress</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ABOUT ===== */}
      <section id="about" className="border-b border-[#DCE3ED] bg-white">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="fx-mono-tag mb-3">§ 01 — ABOUT</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">Automation built around real requirements.</h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-5 text-base sm:text-lg text-[#475569] leading-relaxed">
            <p>Agenaix is a developing AI automation platform for turning business requirements into clearly scoped digital systems and workflows.</p>
            <p className="text-[#64748B]">The platform is currently in pre-launch preparation. Public registration and commercial onboarding are not yet open.</p>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section id="services" className="border-b border-[#DCE3ED] bg-[#F4F6F8]">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10 sm:mb-14">
            <div className="md:col-span-4">
              <div className="fx-mono-tag mb-3">§ 02 — SERVICES</div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">What we build.</h2>
            </div>
            <p className="md:col-span-6 md:col-start-7 text-base sm:text-lg text-[#475569] leading-relaxed">Six initial service categories. Requirements, availability, scope, and pricing will be assessed individually after launch.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {SERVICES.map((s) => {
              const Icon = SVC_ICON[s.key];
              return (
                <div key={s.key} className="border border-[#DCE3ED] bg-white p-6 rounded-xl group hover:shadow-[0_2px_12px_rgba(11,31,58,0.06)] hover:border-[#c8d3e2] transition-all" data-testid={`service-card-${s.key}`}>
                  <div className="flex items-start justify-between mb-6">
                    <span className="fx-mono-tag">{s.code}</span>
                    <Icon size={26} weight="duotone" className="text-[#0B4DCC]" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold tracking-tight mb-2 text-[#172033]">{s.name}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed">{s.blurb}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== CAPABILITIES ===== */}
      <section id="capabilities" className="border-b border-[#DCE3ED] bg-white">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10 sm:mb-14">
            <div className="md:col-span-4">
              <div className="fx-mono-tag mb-3">§ 03 — CAPABILITIES</div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">The full AI stack.</h2>
            </div>
            <p className="md:col-span-6 md:col-start-7 text-base sm:text-lg text-[#475569] leading-relaxed">Six capability layers we assemble into every engagement.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { code: "CAP-01", title: "AI Agents",             desc: "Autonomous, tool-using agents integrated into your operations." },
              { code: "CAP-02", title: "Workflow Automation",   desc: "Cross-app orchestration designed around your team's real workflow." },
              { code: "CAP-03", title: "n8n Integration",       desc: "Visual automation pipelines designed around approved workflows." },
              { code: "CAP-04", title: "WhatsApp Automation",   desc: "Conversational workflows using approved provider capabilities." },
              { code: "CAP-05", title: "AI Model Integration",  desc: "Model selection and prompt controls matched to the use case." },
              { code: "CAP-06", title: "Custom Software Dev",   desc: "Bespoke SaaS, internal tools, admin panels built to your requirements." },
            ].map((c) => (
              <div key={c.code} className="border border-[#DCE3ED] bg-white p-6 rounded-xl" data-testid={`capability-${c.code.toLowerCase()}`}>
                <div className="fx-mono-tag mb-3">{c.code}</div>
                <h3 className="text-base sm:text-lg font-semibold tracking-tight mb-2 text-[#172033]">{c.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PORTFOLIO (neutral placeholder — no invented data) ===== */}
      <section id="portfolio" className="border-b border-[#DCE3ED] bg-[#F4F6F8]">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10">
            <div className="md:col-span-4">
              <div className="fx-mono-tag mb-3">§ 04 — WORK</div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">Example solution areas.</h2>
            </div>
            <p className="md:col-span-6 md:col-start-7 text-base sm:text-lg text-[#475569] leading-relaxed">Illustrative examples of systems Agenaix is being designed to support. These are not customer case studies.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { code: "W-01", label: "AI CHATBOT",     desc: "Domain-trained conversational assistant deployed across web and messaging channels." },
              { code: "W-02", label: "AUTOMATION",     desc: "Cross-team workflow automation removing repetitive manual handoffs." },
              { code: "W-03", label: "BOOKING SYSTEM", desc: "Availability engine with reminders, staff schedules and payment collection." },
              { code: "W-04", label: "CRM PLATFORM",   desc: "Custom pipeline with intent scoring and post-call operations automation." },
            ].map((p) => (
              <div key={p.code} className="border border-[#DCE3ED] bg-white p-6 rounded-xl" data-testid={`portfolio-item-${p.code.toLowerCase()}`}>
                <div className="fx-mono-tag mb-3">{p.code} · {p.label}</div>
                <p className="text-sm sm:text-base text-[#172033] leading-relaxed">{p.desc}</p>
                <div className="mt-5 flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#64748B]">
                  <span>Illustrative capability</span><ArrowRight size={12} weight="bold" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== DELIVERY APPROACH ===== */}
      <section id="approach" className="border-b border-[#DCE3ED] bg-white">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10 sm:mb-14">
            <div className="md:col-span-4">
              <div className="fx-mono-tag mb-3">§ 05 — APPROACH</div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">A controlled delivery process.</h2>
            </div>
            <p className="md:col-span-6 md:col-start-7 text-base sm:text-lg text-[#475569] leading-relaxed">Commercial terms will be provided only after launch, requirement review, and written confirmation of scope.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PLANS.map((p) => (
              <div key={p.name} className={`p-8 rounded-xl flex flex-col border ${p.accent ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "bg-white border-[#DCE3ED]"}`} data-testid={`pricing-plan-${p.name.toLowerCase()}`}>
                <div className="fx-mono-tag" style={{color: p.accent ? "#94a3b8" : undefined}}>{p.name.toUpperCase()}</div>
                <div className={`mt-2 text-sm ${p.accent ? "text-[#94a3b8]" : "text-[#64748B]"}`}>{p.tag}</div>
                <div className={`mt-6 text-2xl sm:text-3xl font-semibold tracking-tight ${p.accent ? "text-white" : "text-[#172033]"}`}>
                  Defined after review
                </div>
                <ul className={`mt-6 space-y-3 text-sm ${p.accent ? "text-[#e2e8f0]" : "text-[#475569]"}`}>
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={16} weight="bold" className={p.accent ? "text-[#4F8CFF] mt-0.5 shrink-0" : "text-[#0B4DCC] mt-0.5 shrink-0"} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className="mt-8">
                  <Button className={`w-full h-11 rounded-lg text-sm font-semibold ${
                    p.accent ? "bg-white text-[#0B1F3A] hover:bg-[#F4F6F8]" : "fx-btn-primary"
                  }`} data-testid={`pricing-cta-${p.name.toLowerCase()}`}>
                    {p.cta} <ArrowUpRight size={14} weight="bold" className="ml-2" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="border-b border-[#DCE3ED] bg-white">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="fx-mono-tag mb-3">§ 06 — FAQ</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">Answers.</h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 border-t border-[#DCE3ED]">
            {[
              { q: "Is Agenaix open to customers now?", a: "Not yet. Agenaix is in pre-launch preparation and public registration is currently closed." },
              { q: "What services are planned?", a: "The initial scope includes websites, CRM platforms, booking systems, AI chatbots, automation, and internal workflows." },
              { q: "How will delivery time be determined?", a: "Any future delivery estimate will depend on confirmed requirements, integrations, risks, and a written project scope." },
              { q: "Can Agenaix integrate different AI models?", a: "The platform is being designed to support model selection based on the use case, technical fit, availability, and approved terms." },
              { q: "Will hosting and maintenance be available?", a: "Possible hosting and maintenance arrangements will be assessed individually and confirmed only in writing." },
              { q: "How will pricing work?", a: "No public prices are currently offered. Future pricing will follow requirement review and a written proposal." },
            ].map((f, i) => (
              <details key={i} className="group border-b border-[#DCE3ED] py-5" data-testid={`faq-item-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="text-base sm:text-lg font-semibold tracking-tight pr-6 text-[#172033]">{f.q}</span>
                  <span className="text-[#0B4DCC] group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <p className="mt-3 text-sm sm:text-base text-[#475569] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CONTACT / CTA ===== */}
      <section id="contact" className="border-b border-[#DCE3ED] bg-[#F4F6F8]">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-6">
            <div className="fx-mono-tag mb-3">§ 07 — CONTACT</div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">Launch preparation underway.</h2>
            <p className="mt-5 text-base sm:text-lg text-[#475569] max-w-md leading-relaxed">
              Public registration and commercial onboarding are currently closed.
            </p>
          </div>
          <div className="md:col-span-5 md:col-start-8 border border-[#DCE3ED] p-8 sm:p-10 bg-white rounded-xl">
            <div className="fx-mono-tag mb-3">PRE-LAUNCH</div>
            <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-[#172033]">Registration is currently closed</h3>
            <p className="mt-2 text-sm text-[#475569]">Existing authorised users can continue to sign in.</p>
            <div className="mt-6 space-y-3">
              <Link to="/signup">
                <Button className="fx-btn-accent h-11 w-full rounded-lg text-sm font-semibold" data-testid="contact-cta-signup">
                  View launch status <ArrowUpRight size={14} weight="bold" className="ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="h-11 w-full rounded-lg text-sm font-semibold border-[#DCE3ED]" data-testid="contact-cta-login">
                  I already have an account
                </Button>
              </Link>
            </div>
            <div className="mt-6 pt-5 border-t border-[#DCE3ED] text-xs text-[#64748B]">
              <span>
                Read our <Link to="/terms" className="text-[#0B4DCC] hover:underline">Terms of Use</Link>
                {" and "}
                <Link to="/privacy" className="text-[#0B4DCC] hover:underline">Privacy Policy</Link>.
              </span>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-6 sm:py-8 border-t border-[#DCE3ED] bg-white">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-xs">
          <span className="fx-mono-tag">© 2026 Agenaix · Pre-launch project.</span>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/privacy" className="text-[#0B4DCC] hover:underline">Privacy Policy</Link>
            <Link to="/terms" className="text-[#0B4DCC] hover:underline">Terms of Use</Link>
            <a href="mailto:agenaix.ai@gmail.com" className="text-[#0B4DCC] hover:underline">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
